/**
 * @module app.ext.iccc
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, } from 'app/shared/lib/essette-site';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { AssessmentService } from 'app/core/services/assessment-service';
import { InterQualCoordinatedCareContentService } from 'app/ext/iccc/services/iccc-service';
import { AssessmentContext } from 'app/ext/iccc/assessment-context';
import { ExtendLocalState } from 'app/shared/models/local-state-extensions';
import { IBaseLocalState } from 'app/shared/models/local-state-extensions';
import { IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { fireEvents } from 'app/shared/lib/fire-events';
import { getReference } from 'app/shared/models/essette-types';
import * as Iccc from 'app/ext/iccc/interfaces';
import * as CM from 'app/cm/models/essette-api';
import * as Core from 'app/core/models/essette-api';
import { TitleChangeEvent } from 'app/shared/lib/events';
import { EventAggregator } from 'aurelia-event-aggregator';

export type CaseAssessmentAttachmentWithLocalState = ExtendLocalState<CM.CaseAssessmentAttachment, IBaseLocalState>;

@autoinject()
export class AssessmentAttachmentCustomElement extends ComponentBase {

    ////****************** Fields and Properties **************************************////

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public config: IAssessmentsConfig | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public attachmentId: string | null = null;

    @bindable
    public mode: string | 'assessment' | 'careplan' = 'assessment';

    protected viewerUrl: string | null = null;

    protected attachment: CaseAssessmentAttachmentWithLocalState | null = null;
    protected session: Core.AssessmentSession | null = null;
    protected carePlanGoals: CM.CarePlanGoal[] = [];

    protected complete: boolean = false;

    protected stateMessage: string = '';

    ////****************** Lifecycle events (in order of execution) ******************////

    constructor(
        private readonly $element: Element,
        private readonly context: AssessmentContext,
        private readonly service: AssessmentService,
        private readonly icccService: InterQualCoordinatedCareContentService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected bind(): void {
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }
        if(!this.attachmentId) { throw new Error('Bindable property [attachment-id] was not set'); }

        this.service.configure(this.config);

        this.load();
    }

    ////****************** Functions ***********************************************////

    private async load(): Promise<void> {
        if(!this.attachmentId) return;

        try {
            this.stateMessage = 'Loading Assessment...';
            this.state.beginLoad();

            //Clears any cached assessment or careplan content on the ICCC server
            await this.clearViewer();

            await this.loadAttachment();

            //External content might not exist if attachment was created via a Program
            if(!this.attachment?.externalContent) {
                await this.createAssessmentExternalContent('{}');
            }

            if(!this.attachment?.externalCarePlanContent) {
                await this.createCarePlanExternalContent('[]');
            }

            //Reload attachment if any external content is newly created
            if(!this.attachment?.externalContent || !this.attachment?.externalCarePlanContent) {
                await this.loadAttachment();
            }

            // Set Page title to Assessment name
            if(!!this.config?.backRouteName && !!this.attachment?.$display) {
                this.events.publish(new TitleChangeEvent([this.attachment.$display]));
            }

            await this.loadCurrentCarePlan();
            await this.loadViewer();

            if(this.mode === 'assessment') await this.loadAssessmentViewerUrl();
            else if(this.mode === 'careplan') await this.loadCarePlanViewerUrl();
        }
        catch(error) {
            this.logger.error('Error loading assessment');
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    protected async saveAssessmentAndReturnToCase(): Promise<void> {
        try {
            this.stateMessage = 'Saving Assessment...';
            this.state.beginSave();

            if(!this.attachment?.isCompleted) {
                await this.saveAssessment();
                await this.saveCarePlan();
            }
        }
        catch(error) {
            this.logger.error('Error saving assessments');
            throw error;
        }
        finally {
            this.state.endSave();
        }

        this.router.navigate(`#/cm/case/${this.attachment?.case?.id}/careplan`);
    }

    private async saveAssessment(): Promise<void> {
        if(!this.attachmentId || !this.attachment) throw new Error('Error saving assessment');

        const assessment: CM.CaseAssessmentWithVersionModel | null | undefined = this.attachment.assessment;
        if(!assessment) { throw new Error('Error saving assessment'); }

        const icccAssessment: Iccc.Assessment | null = await this.loadAssessment();
        if(!icccAssessment) throw new Error('Error saving assessment');

        if(this.attachment.lastSession && this.attachment.lastSession.id) await this.loadSession(this.attachment.lastSession.id);

        const externalContentId: string | null | undefined = this.attachment?.externalContent?.id;
        if(!externalContentId) throw new Error('Error saving assessment');

        const externalContent: Core.AssessmentAttachmentExternalContent = { content: JSON.stringify(icccAssessment) };
        await this.updateExternalContent(externalContentId, externalContent);

        const answers: Core.AssessmentAnswer[] = this.getAnswers(icccAssessment, assessment);

        this.context.attachment = this.attachment;
        this.context.currentSession = {
            id: this.session?.id,
            attachment: getReference(this.attachment),
            answers: answers
        };

        this.saveSession();
        if(this.complete) await this.completeAttachment();
        this.toaster.standard.saved('am:assessment.$');
    }

    private async saveCarePlan(): Promise<void> {
        if(!this.attachmentId || !this.attachment || !this.attachment.externalCarePlanContent?.id) throw new Error('Error saving careplan');

        const icccCarePlan: Iccc.CarePlan | null = await this.loadCarePlan();
        if(!icccCarePlan) throw new Error('Error saving careplan');

        const externalContentString: string = JSON.stringify(icccCarePlan.goals);

        const attachment: CM.CaseAssessmentAttachment = {
            id: this.attachment.id,
            attachedTo: { id: this.attachment.attachedTo?.id },
            assessment: {
                externalAssessmentIdentifier: this.attachment.assessment?.externalAssessmentIdentifier,
                currentVersion: { versionName: this.attachment.assessment?.currentVersion?.versionName }
            },
            externalCarePlanContent: { content: externalContentString }
        };

        this.createCarePlan(attachment);
    }

    private getAnswers(icccAssessment: Iccc.Assessment, assessment: CM.CaseAssessmentWithVersionModel): Core.AssessmentAnswer[] {
        const answers = new Array<Core.AssessmentAnswer>();
        let complete = true;

        for(const s of icccAssessment.iccc_sections) {
            complete = complete && s.complete;

            for(const ss of s.iccc_sub_sections) {
                const coreSection = assessment?.currentVersion?.sections?.find(section => section.externalSectionIdentifier === ss.id);
                if(!coreSection) continue;

                for(const q of ss.iccc_questions) {
                    const coreQuestion = coreSection.questions?.find(question => question.externalQuestionIdentifier === q.id);
                    if(!coreQuestion) continue;

                    if(coreQuestion.type?.code === 'Text' || coreQuestion.type?.code === 'Numeric' || coreQuestion.type?.code === 'Sum' || coreQuestion.type?.code === 'Calc') {
                        if(!q.response) continue;

                        const answer: Core.AssessmentAnswer = {
                            text: q.response,
                            question: { id: coreQuestion.id }
                        };

                        answers.push(answer);
                    }
                    else {
                        for(const c of q.iccc_choices) {
                            if(c.response) {
                                const coreChoice = coreQuestion.choices?.find(choice => choice.externalChoiceIdentifier === c.id);
                                if(!coreChoice) continue;

                                const answer: Core.AssessmentAnswer = {
                                    choice: {
                                        id: coreChoice.id,
                                        externalChoiceIdentifier: c.id
                                    },
                                    question: { id: coreQuestion.id }
                                };

                                answers.push(answer);
                            }
                        }
                    }
                }
            }
        }

        this.complete = complete;
        return answers;
    }

    private async createAssessmentExternalContent(content: string): Promise<void> {
        const externalContent: Core.AssessmentAttachmentExternalContent = {
            assessmentAttachment: { id: this.attachmentId },
            content: content,
            mediaType: { code: 'data.json' },
            externalContentType: { code: 'AssessmentJson' }
        };

        await this.insertExternalContent(externalContent);
    }

    private async createCarePlanExternalContent(content: string): Promise<void> {
        const externalContent: Core.AssessmentAttachmentExternalContent = {
            assessmentAttachment: { id: this.attachmentId },
            content: content,
            mediaType: { code: 'data.json' },
            externalContentType: { code: 'CareplanJson' }
        };

        await this.insertExternalContent(externalContent);
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async clearViewer(): Promise<void> {
        if(!this.attachmentId) return;

        try {
            this.state.beginLoad();

            await this.icccService.clearViewer(this.attachmentId);
            await this.icccService.clearCarePlanViewer(this.attachmentId);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadViewer(): Promise<void> {
        if(!this.attachmentId) return;

        try {
            this.state.beginLoad();

            await this.icccService.loadViewer(this.attachmentId);
            await this.icccService.loadCarePlanViewer(this.attachmentId);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadAssessmentViewerUrl(): Promise<void> {
        if(!this.attachmentId) return;

        try {
            this.state.beginLoad();

            const response = await this.icccService.getViewerUrl(this.attachmentId);
            if(!response.isSuccessful) return;

            this.viewerUrl = response.result;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCarePlanViewerUrl(): Promise<void> {
        if(!this.attachmentId) return;

        try {
            this.state.beginLoad();

            const response = await this.icccService.getCarePlanViewerUrl(this.attachmentId);
            if(!response.isSuccessful) return;

            this.viewerUrl = response.result;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadAssessment(): Promise<Iccc.Assessment | null> {
        if(!this.attachmentId) return null;

        try {
            const response = await this.icccService.getAssessmentContent(this.attachmentId);
            if(!response.isSuccessful) return null;

            const assessment: Iccc.Assessment = response.result;
            return assessment;
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall
    private async loadAttachment(): Promise<void> {
        if(!this.attachmentId) return;

        const includes: string[] = [
            'assessment',
            'assessment.currentVersion',
            'assessment.currentVersion.sections',
            'assessment.currentVersion.sections.questions',
            'assessment.currentVersion.sections.questions.choices',
            'assessment.currentVersion.sections.questions.type',
            'externalContent',
            'externalCarePlanContent'
        ];

        try {
            const response = await this.service.loadAttachment(this.attachmentId, includes);
            if(!response.isSuccessful || response.results.length === 0) return;

            const attachment: CM.CaseAssessmentAttachment = response.results[0];
            this.attachment = attachment;
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall
    private async loadCarePlan(): Promise<Iccc.CarePlan | null> {
        if(!this.attachmentId) return null;

        try {
            const response = await this.icccService.getCarePlan(this.attachmentId);
            if(!response.isSuccessful) return null;

            const carePlan: Iccc.CarePlan = response.result;
            return carePlan;
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall
    private async loadCurrentCarePlan(): Promise<boolean> {
        if(!this.attachment || !this.attachment.case || !this.attachment.case.id) return Promise.resolve(false);
        const caseId: string = this.attachment!.case!.id!;

        const includes = ['problem', 'interventions', 'externalSystem'];

        try {
            const response = await this.icccService.loadCaseIcccGoals(caseId, includes);

            if(response.isSuccessful) {
                this.carePlanGoals = response.results;
                return Promise.resolve(true);
            }

            return Promise.resolve(false);
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall
    private async createCarePlan(attachment: CM.CaseAssessmentAttachment): Promise<void> {
        try {
            const response = await this.icccService.saveCarePlan(attachment);
            if(response.isSuccessful) this.toaster.standard.saved('cm:carePlan.$', true);
        }
        catch(error) {
            this.toaster.standard.saved('cm:carePlan.$', false);
            throw error;
        }
    }

    @apiCall
    private async loadSession(sessionId: string): Promise<void> {
        const includes: string[] = ['metadata.modified.byUser'];

        try {
            const response = await this.service.loadSession(sessionId, includes);
            if(!response.isSuccessful || response.results.length === 0) return;
            this.session = response.results[0];
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall
    private async saveSession(): Promise<boolean> {
        if(!this.context.currentSession) { return false; }

        try {
            const response = await this.service.saveSession(this.context.currentSession);

            if(response.isSuccessful) {
                this.context.currentSession.id = response.id;
            }
            else {
                this.toaster.standard.saved('am:assessment.$', false);
                throw new Error(response.resultText || 'Unable to save assessment');
            }

            return response.isSuccessful;
        }
        catch(error) {
            this.toaster.standard.saved('am:assessment.$', false);
            throw error;
        }
    }

    @apiCall
    private async completeAttachment(): Promise<boolean> {
        if(!this.attachmentId) { return false; }

        try {
            const response = await this.service.completeAttachment(this.attachmentId);
            this.toaster.standard.completed('am:assessment.$', response.isSuccessful);

            if(response.isSuccessful) {
                fireEvents(this.$element, 'assessment-complete');
            }
            return response.isSuccessful;
        }
        catch(error) {
            this.toaster.standard.completed('am:assessment.$', false);
            throw error;
        }
    }

    @apiCall
    private async insertExternalContent(externalContent: Core.AssessmentAttachmentExternalContent): Promise<void> {
        try {
            await this.service.insertAttachmentExternalContent(externalContent);
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall
    private async updateExternalContent(id: string, externalContent: Core.AssessmentAttachmentExternalContent): Promise<void> {
        try {
            await this.service.updateAttachmentExternalContent(id, externalContent);
        }
        catch(error) {
            throw error;
        }
    }

}
